import { CommonModule } from '@angular/common';
import { Component, inject } from '@angular/core';
import {
  FormControl,
  FormGroup,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { CategoryService } from 'src/app/services/category';
import { LocaleService } from 'src/app/services/locale';

interface Question8Answers {
  hasInitiatives: boolean | null;
  initiatives: string[];
  otherInitiativeText: string;
  spatialTemporalBoundaries: string;
  quantificationApproach: string;
  quantificationNotes: string;
  differenceDetermination: string;
}

@Component({
  selector: 'app-category1-question8',
  imports: [CommonModule, ReactiveFormsModule],
  template: `
    <div class="flex flex-col gap-6">

      <form [formGroup]="form" class="flex flex-col gap-6">
        <!-- Step 1: Applicability -->
        <div class="bg-white p-4 sm:p-6 rounded-lg border border-[#DEE2E6]">
          <h4 class="text-sm sm:text-lg font-medium text-[#212529] mb-4">
            {{ strings[language].step1Title }}
          </h4>
          <p class="text-[14px] text-[#616161] mb-4">{{ strings[language].step1Question }}</p>
          <div class="flex gap-4">
            <label class="flex items-center gap-2 cursor-pointer">
              <input
                type="radio"
                [value]="true"
                formControlName="hasInitiatives"
                (change)="onChange()"
                class="w-4 h-4  accent-[#183a25]"
              />
              <span class="text-[#212529]">{{ strings[language].yes }}</span>
            </label>
            <label class="flex items-center gap-2 cursor-pointer">
              <input
                type="radio"
                [value]="false"
                formControlName="hasInitiatives"
                (change)="onChange()"
                class="w-4 h-4 accent-[#183a25]"
              />
              <span class="text-[#212529]">{{ strings[language].no }}</span>
            </label>
          </div>
        </div>

        <!-- Conditional Steps (only show if hasInitiatives is true) -->
        <ng-container *ngIf="form.controls['hasInitiatives'].value === true">
          <!-- Step 2: Description of Initiatives -->
          <div class="bg-white p-4 sm:p-6 rounded-lg border border-[#DEE2E6]">
            <h4 class="text-sm sm:text-lg font-medium text-[#212529] mb-4">
              {{ strings[language].step2Title }}
            </h4>
            <p class="text-[14px] text-[#616161] mb-4">
              {{ strings[language].step2Question }}
            </p>

            <div class="flex flex-col gap-3">
              <label
                *ngFor="let option of initiativeOptions"
                class="flex items-start gap-2 cursor-pointer"
              >
                <input
                  type="checkbox"
                  [value]="option.value"
                  (change)="onInitiativeChange($event)"
                  [checked]="isInitiativeSelected(option.value)"
                  class="w-4 h-4 mt-1  accent-[#183a25]"
                />
                <span class="text-[#212529]">{{
                  strings[language].initiatives[option.value]
                }}</span>
              </label>

              <!-- Other initiative text field -->
              <div
                *ngIf="isInitiativeSelected('other')"
                class="ml-6 mt-2"
              >
                <input
                  type="text"
                  formControlName="otherInitiativeText"
                  (change)="onChange()"
                  placeholder="{{ strings[language].specifyPlaceholder }}"
                  class="p-3 rounded-lg border w-full border-[#CED4DA] focus:outline-none focus:border-blue-500"
                />
              </div>
            </div>
          </div>

          <!-- Step 3: Spatial and Temporal Boundaries -->
          <div class="bg-white p-4 sm:p-6 rounded-lg border border-[#DEE2E6]">
            <h4 class="text-sm sm:text-lg font-medium text-[#212529] mb-4">
              {{ strings[language].step3Title }}
            </h4>

            <div class="flex justify-between ">


            <p class="text-[14px] text-[#616161] mb-4">
              {{ strings[language].step3Question }}
            </p>

             <div class="relative group ml-1 mt-1">
                <button
                  type="button"
                  class="text-gray-500 hover:text-gray-700 focus:outline-none"
                  (click)="$event.stopPropagation()"
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    class="h-5 w-5"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      stroke-linecap="round"
                      stroke-linejoin="round"
                      stroke-width="2"
                      d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                    />
                  </svg>
                </button>
                <div
                  class="absolute z-10 hidden group-hover:block w-[400px] bg-white p-2 border border-gray-200 rounded shadow-lg text-sm left-0 bottom-full mb-2"
                >
                  <p class="mb-2">{{ strings[language].averageMethodDescLine1 }}</p>
<p class="mb-2">{{ strings[language].averageMethodDescLine2 }}</p>
<p class="text-sm text-[#495057]">
                {{ strings[language].step3Tooltip }}
              </p>
                </div>
              </div>
              </div>

            <!-- <div class="bg-blue-50 border border-blue-200 rounded p-3 mb-4">
              <p class="text-sm text-[#495057]">
                {{ strings[language].step3Tooltip }}
              </p>
            </div> -->

            <textarea
              formControlName="spatialTemporalBoundaries"
              (change)="onChange()"
              rows="4"
              placeholder="{{ strings[language].step3Placeholder }}"
              class="p-3 rounded-lg border w-full border-[#CED4DA] focus:outline-none focus:border-blue-500"
            ></textarea>
          </div>

          <!-- Step 4: Quantification Approach -->
          <div class="bg-white p-4 sm:p-6 rounded-lg border border-[#DEE2E6]">
            <h4 class="text-sm sm:text-lg font-medium text-[#212529] mb-4">
              {{ strings[language].step4Title }}
            </h4>
            <p class="text-[14px] text-[#616161] mb-4">
              {{ strings[language].step4Question }}
            </p>


            <select
              formControlName="quantificationApproach"
              (change)="onChange()"
              class="p-3 rounded-lg border w-full border-[#CED4DA] focus:outline-none focus:border-blue-500 bg-white mb-4"
            >
              <option value="">{{ strings[language].selectOption }}</option>
              <option
                *ngFor="let option of quantificationOptions"
                [value]="option.value"
              >
                {{ strings[language].quantificationApproaches[option.value] }}
              </option>
            </select>

            <textarea
              formControlName="quantificationNotes"
              (change)="onChange()"
              rows="3"
              placeholder="{{ strings[language].optionalNotesPlaceholder }}"
              class="p-3 rounded-lg border w-full border-[#CED4DA] focus:outline-none focus:border-blue-500"
            ></textarea>
          </div>

          <!-- Step 5: Determination and Classification (Optional) -->
          <div class="bg-white p-4 sm:p-6 rounded-lg border border-[#DEE2E6]">
            <h4 class="text-sm sm:text-lg font-medium text-[#212529] mb-2">
              {{ strings[language].step5Title }}
            </h4>
            <p class="text-sm text-[#6C757D] mb-4">
              {{ strings[language].optional }}
            </p>

            <div class="flex">
            <p class="text-[14px] text-[#616161] mb-4">
              {{ strings[language].step5Question }}
            </p>
             <div class="relative group ml-1 mt-1">
                <button
                  type="button"
                  class="text-gray-500 hover:text-gray-700 focus:outline-none"
                  (click)="$event.stopPropagation()"
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    class="h-5 w-5"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      stroke-linecap="round"
                      stroke-linejoin="round"
                      stroke-width="2"
                      d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                    />
                  </svg>
                </button>
                <div
                  class="absolute z-10 hidden group-hover:block w-[400px] bg-white p-2 border border-gray-200 rounded shadow-lg text-sm left-0 bottom-full mb-2"
                >
                  <p class="mb-2">{{ strings[language].averageMethodDescLine1 }}</p>
<p class="mb-2">{{ strings[language].averageMethodDescLine2 }}</p>
   <p class="text-sm text-[#495057]">
                {{ strings[language].step5Tooltip }}
              </p>
                </div>
              </div>
              </div>

            <!-- <div class="bg-blue-50 border border-blue-200 rounded p-3 mb-4">
              <p class="text-sm text-[#495057]">
                {{ strings[language].step5Tooltip }}
              </p>
              </div> -->

            <textarea
              formControlName="differenceDetermination"
              (change)="onChange()"
              rows="4"
              placeholder="{{ strings[language].step5Placeholder }}"
              class="p-3 rounded-lg border w-full border-[#CED4DA] focus:outline-none focus:border-blue-500"
            ></textarea>
          </div>
        </ng-container>
      </form>
    </div>
  `,
})
export class Category1Question8Component {
  language = inject(LocaleService).getLanguage();
  categoryService = inject(CategoryService);
  categories = this.categoryService.categories();

  form = new FormGroup({
    hasInitiatives: new FormControl<boolean | null>(null, Validators.required),
    initiatives: new FormControl<string[]>([]),
    otherInitiativeText: new FormControl(''),
    spatialTemporalBoundaries: new FormControl(''),
    quantificationApproach: new FormControl(''),
    quantificationNotes: new FormControl(''),
    differenceDetermination: new FormControl(''),
  });

  initiativeOptions = [
    { value: 'reforestation' },
    { value: 'biochar' },
    { value: 'ccs' },
    { value: 'renewable' },
    { value: 'efficiency' },
    { value: 'other' },
  ];

  quantificationOptions = [
    { value: 'ipcc' },
    { value: 'iso' },
    { value: 'emission_factors' },
    { value: 'lca' },
    { value: 'other' },
  ];

  strings: any = {
    english: {
      title: 'Direct GHG Removals and Mitigation',
      subtitle: 'Q8 - Rimozioni e mitigazioni dirette di GHG',
      yes: 'Yes',
      no: 'No',
      optional: '(Optional)',
      selectOption: 'Select an option...',
      specifyPlaceholder: 'Please specify...',
      optionalNotesPlaceholder: 'Optional notes...',

      step1Title: 'Step 1 – Applicability',
      step1Question:
        'Does your organization account for or implement any direct greenhouse gas (GHG) removal or mitigation initiatives?',

      step2Title: 'Step 2 – Description of Initiatives',
      step2Question:
        'Which direct GHG removal or reduction initiatives have been implemented?',
      initiatives: {
        reforestation: 'Reforestation or sustainable forest management',
        biochar: 'Biochar or soil carbon sequestration',
        ccs: 'Carbon capture and storage (CCS/CCU)',
        renewable: 'Replacement of fossil fuels with on-site renewable sources',
        efficiency: 'Improvement of direct process energy efficiency',
        other: 'Other (please specify)',
      },

      step3Title: 'Step 3 – Spatial and Temporal Boundaries',
      step3Question:
        'What are the spatial and temporal boundaries of these initiatives?',
      step3Tooltip:
        '• Geographic area (facility, site, plot)\n• Timeframe (start, end, recurrence)',
      step3Placeholder:
        'Describe the geographic area and timeframe of the initiatives...',

      step4Title: 'Step 4 – Quantification Approach',
      step4Question:
        'What approach is used to quantify differences in GHG emissions or removals?',
      quantificationApproaches: {
        ipcc: 'IPCC Guidelines (2006 or 2019)',
        iso: 'ISO Standards (14064-2 or 14067)',
        emission_factors:
          'Standard emission factors (e.g., Climatiq, DEFRA, EPA)',
        lca: 'LCA-based or project-specific models',
        other: 'Other (please specify)',
      },

      step5Title: 'Step 5 – Determination and Classification of Differences',
      step5Question:
        'How are the differences in emissions or removals attributable to these initiatives determined and classified?',
      step5Tooltip:
        'Describe baseline definition, before/after comparison method, or third-party verification if applicable.',
      step5Placeholder:
        'Describe your methodology for determining and classifying differences...',
    },
    italian: {
      title: 'Rimozioni e mitigazioni dirette di GHG',
      subtitle: 'Q8 - Direct GHG Removals and Mitigation',
      yes: 'Sì',
      no: 'No',
      optional: '(Facoltativo)',
      selectOption: 'Seleziona un\'opzione...',
      specifyPlaceholder: 'Specificare...',
      optionalNotesPlaceholder: 'Note facoltative...',

      step1Title: 'Step 1 – Applicabilità',
      step1Question:
        'La vostra organizzazione contabilizza o implementa iniziative di rimozione o mitigazione diretta di gas a effetto serra (GHG)?',

      step2Title: 'Step 2 – Descrizione delle iniziative',
      step2Question:
        'Quali iniziative di rimozione o riduzione diretta di GHG sono state implementate?',
      initiatives: {
        reforestation: 'Riforestazione o gestione forestale sostenibile',
        biochar: 'Biochar o sequestro del carbonio nel suolo',
        ccs: 'Cattura e stoccaggio del carbonio (CCS/CCU)',
        renewable:
          'Sostituzione di combustibili fossili con fonti rinnovabili on-site',
        efficiency:
          'Miglioramento dell\'efficienza energetica dei processi diretti',
        other: 'Altre (specificare)',
      },

      step3Title: 'Step 3 – Confini spaziali e temporali',
      step3Question:
        'Quali sono i confini spaziali e temporali di queste iniziative?',
      step3Tooltip:
        '• Area geografica (impianto, sede, appezzamento)\n• Periodo di applicazione (inizio, fine, ricorrenza)',
      step3Placeholder:
        'Descrivere l\'area geografica e il periodo delle iniziative...',

      step4Title: 'Step 4 – Approccio di quantificazione',
      step4Question:
        'Qual è l\'approccio utilizzato per quantificare le differenze nelle emissioni o negli assorbimenti di GHG?',
      quantificationApproaches: {
        ipcc: 'Linee guida IPCC (2006 o 2019)',
        iso: 'Standard ISO (14064-2 o 14067)',
        emission_factors:
          'Fattori di emissione standard (es. Climatiq, DEFRA, EPA)',
        lca: 'Analisi LCA o modelli di progetto specifici',
        other: 'Altro (specificare)',
      },

      step5Title: 'Step 5 – Determinazione e classificazione delle differenze',
      step5Question:
        'Come vengono determinate e classificate le differenze di emissioni o rimozioni attribuibili alle iniziative di riduzione?',
      step5Tooltip:
        'Descrivere eventuale baseline, metodo di confronto ante/post intervento, o verifiche indipendenti.',
      step5Placeholder:
        'Descrivere la metodologia per determinare e classificare le differenze...',
    },
  };

  ngOnInit(): void {
    let categories = localStorage.getItem('categories');
    if (categories) {
      categories = JSON.parse(categories);
      // @ts-ignore
      if (categories?.[0]?.questions?.[7]?.answers) {
        // @ts-ignore
        const answers = categories[0].questions[7].answers;
        this.form.patchValue(answers);
      }
    }
    this.form.controls['hasInitiatives'].valueChanges.subscribe(value => {
      if (value === false) {
        this.clearInitiativeData();
      }
    });
  }

  clearInitiativeData(): void {
    this.form.patchValue({
      initiatives: [],
      otherInitiativeText: '',
      spatialTemporalBoundaries: '',
      quantificationApproach: '',
      quantificationNotes: '',
      differenceDetermination: '',
    });

    this.onChange();
  }


  isInitiativeSelected(value: string): boolean {
    const initiatives = this.form.controls['initiatives'].value || [];
    return initiatives.includes(value);
  }

  onInitiativeChange(event: Event): void {
    const checkbox = event.target as HTMLInputElement;
    const value = checkbox.value;
    const currentInitiatives = this.form.controls['initiatives'].value || [];

    if (checkbox.checked) {
      this.form.controls['initiatives'].setValue([...currentInitiatives, value]);
    } else {
      this.form.controls['initiatives'].setValue(
        currentInitiatives.filter((v) => v !== value)
      );

      // Clear "other" text if unchecked
      if (value === 'other') {
        this.form.controls['otherInitiativeText'].setValue('');
      }
    }

    this.onChange();
  }

  onChange = async () => {
    // @ts-ignore
    this.categories[0].questions[7].answers = this.form.value;
    this.categoryService.persistCategories(this.categories);
  };
}
